import React from 'react';
import { Theme } from '@seedlang/constants';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import styled from '@emotion/styled';
import { DictionarySentencesWrapper } from '@seedlang/hoc';
import TranslationWithSource from 'components/translation_with_source';
import { AppUI } from '@seedlang/state';
import VocabSentencePopup from 'components/vocab/vocab_sentence_popup';

const Row = styled.div`
  display: flex;
  align-items: stretch;
  margin: 0;
  transition: background 0.15s ease;
  cursor: default;
  &:hover, &:focus-within { background: #F1F1F1; }
  &:active { background: #E5E5E5; }
  &:focus-visible { outline: 2px solid #999; outline-offset: 2px; }
`;

const PlayButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 45px;
  min-height: 46px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 5px;
  color: #999;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  span { display: flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 50%; background: #F0F0F0; }
  &:hover { color: #555; }
  &:hover span { background: #E2E2E2; }
  &:focus-visible { outline: 2px solid #999; outline-offset: 2px; }
  svg { width: 16px; height: 16px; fill: currentColor; }
`;

const Skeleton = styled.div`
  .skeleton-row { display: flex; align-items: center; gap: 15px; min-height: 46px; padding: 7px 10px; box-sizing: border-box; }
  border-top: ${props => props.showSeparators && props.hasLoadedSentences ? '1px solid #CCC' : 'none'};
  .skeleton-row:not(:last-child) { border-bottom: ${props => props.showSeparators ? '1px solid #CCC' : 'none'}; }
  .skeleton-flag { width: 20px; height: 20px; border-radius: 50%; background: #EEE; flex-shrink: 0; }
  .skeleton-text { height: 15px; border-radius: 4px; background: #EEE; }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 10px 0;
  h3 { margin: 0; }
`;

const Pagination = styled.nav`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin: 0;
  flex-shrink: 0;
  font-size: 12px;
  color: #777;
  .page-count {
    min-width: 64px;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }
  button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 32px;
    padding: 0;
    color: inherit;
    background: ${Theme.lightGray};
    border: 0;
    border-radius: 5px;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
    &:hover:not(:disabled) { background: #e2e2e2; }
    &:focus-visible { outline: 2px solid #999; outline-offset: 2px; }
    &:active:not(:disabled) { background: #e5e5e5; }
    &:disabled { cursor: default; opacity: 0.3; }
    svg { width: 24px; height: 24px; fill: none; stroke: currentColor; stroke-width: 2; }
  }
`;

const AdminOnlyTag = styled.span`
  padding: 2px 5px;
  border-radius: 3px;
  background: #EEE;
  color: #666;
  font-size: 10px;
  line-height: 14px;
  font-weight: normal;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

@observer
class DictionarySentences extends React.Component {
  @observable popupSentence;

  endVisible = false;
  endObserver;

  setListEnd = element => {
    this.endObserver?.disconnect();
    this.endVisible = false;
    if (!element) return;
    this.endObserver = new IntersectionObserver(entries => {
      this.endVisible = entries[0].isIntersecting;
      if (this.endVisible) this.props.loadMore();
    });
    this.endObserver.observe(element);
  }

  componentWillUnmount() {
    this.endObserver?.disconnect();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.wordId !== this.props.wordId || prevProps.meaningId !== this.props.meaningId || prevProps.allSentences !== this.props.allSentences) {
      this.popupSentence = null;
    }
    if (this.props.infinite && this.endVisible) this.props.loadMore();
  }

  renderSkeleton = () => (
    <Skeleton showSeparators={this.props.showSeparators} hasLoadedSentences={this.props.store.indexData.length > 0} role='status' aria-label='Loading sentences' aria-busy='true'>
      {Array.from({ length: this.props.infinite ? Math.min(10, Math.max(0, (this.props.store.totalCount ?? 10) - this.props.store.indexData.length)) : 3 }, (_, index) => (
        <div className='skeleton-row' key={index} aria-hidden='true'>
          <span className='skeleton-flag' />
          <span className='skeleton-text' style={{ width: ['78%', '62%', '86%'][index % 3] }} />
        </div>
      ))}
    </Skeleton>
  );

  render() {
    if (this.props.error && !this.props.store.indexData.length) {
      return <div role='alert'>Could not load sentences. <button onClick={() => this.props.retry()}>Retry</button></div>;
    }
    const showSkeleton = !this.props.hideSkeleton && !this.props.store.indexData.length && this.props.loading && (this.props.meaningId || this.props.allSentences);
    if (!showSkeleton && !this.props.store.indexData.length) return this.props.infinite ? <div>No sentences available.</div> : null;
    const pages = Math.ceil(this.props.store.totalCount / 3);
    const pagination = !this.props.meaningId && !this.props.infinite && pages > 1 ? (
      <Pagination aria-label={this.props.title || (this.props.allSentences ? 'Sentences' : 'Meaning sentences')}>
            <button type='button' aria-label='Previous page' title='Previous page' disabled={this.props.loading || this.props.page === 1} onClick={() => this.props.loadPage(this.props.page - 1)}>
              <svg viewBox='0 0 24 24' aria-hidden='true'><path d='M15 5l-7 7 7 7' strokeLinecap='round' strokeLinejoin='round' /></svg>
            </button>
            <span className='page-count' aria-live='polite' aria-label={`Page ${this.props.page} of ${pages}`}>{this.props.page} / {pages}</span>
            <button type='button' aria-label='Next page' title='Next page' disabled={this.props.loading || this.props.page === pages} onClick={() => this.props.loadPage(this.props.page + 1)}>
              <svg viewBox='0 0 24 24' aria-hidden='true'><path d='M9 5l7 7-7 7' strokeLinecap='round' strokeLinejoin='round' /></svg>
            </button>
          </Pagination>
    ) : null;
    return (
      <div>
        {!this.props.meaningId && (!this.props.hideHeader || pagination) && (
          <SectionHeader>
            {!this.props.hideHeader && this.props.title && (
              <SectionTitle>
                <h3>{this.props.title}</h3>
                {this.props.adminOnly && <AdminOnlyTag>admin-only</AdminOnlyTag>}
              </SectionTitle>
            )}
            {pagination}
          </SectionHeader>
        )}
        {showSkeleton ? (
          this.renderSkeleton()
        ) : this.props.store.indexData.map((sentence, index) => {
          const hasVideo = Boolean(sentence.target?.hasVideoClip);
          return (
            <Row key={sentence.id} style={{ paddingTop: index === 0 ? this.props.firstRowPaddingTop : undefined, borderBottom: this.props.showSeparators && index < this.props.store.indexData.length - 1 ? '1px solid #CCC' : undefined }}>
              <TranslationWithSource
                exclusiveSource
                enableRowPressToggle
                stopLanguageClickPropagation
                background='transparent'
                languageButtonBackground='transparent'
                translation={sentence}
                padding='7px 10px'
                fontSize={this.props.fontSize || '16px'}
                languageButtonWrapperWidth='25px'
                languageButtonWrapperPadding='0 5px 0 0'
                showSentenceBreakdown
                sentenceBreakdownProps={{
                  highlightWordId: this.props.wordId,
                  hideWordAssociationHighlight: true,
                  disableOnClick: true,
                }}
                user={AppUI.user}
              />
              {hasVideo && (
                <PlayButton
                  type='button'
                  title='Play video'
                  aria-label={`Play video: ${sentence.targetText}`}
                  onClick={() => this.props.onPlaySentence ? this.props.onPlaySentence(sentence) : this.popupSentence = sentence}
                >
                  <span><svg viewBox='0 0 24 24' aria-hidden='true'><path d='M8 5v14l11-7z' /></svg></span>
                </PlayButton>
              )}
            </Row>
          );
        })}
        {this.props.infinite && <div ref={this.setListEnd} style={{ height: 1 }} aria-hidden='true' />}
        {!this.props.hideSkeleton && this.props.infinite && this.props.store.indexData.length > 0 && this.props.loading && this.renderSkeleton()}
        {this.props.infinite && this.props.error && <div role='alert'>Could not load more sentences. <button onClick={this.props.retry}>Retry</button></div>}
        {this.popupSentence &&
          <VocabSentencePopup
            autoPlay
            popupSentence={this.popupSentence}
            wordId={this.props.wordId}
            onClickSentence={sentence => this.popupSentence = sentence}
          />
        }
      </div>
    );
  }
}

export default DictionarySentencesWrapper(DictionarySentences);
